import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import { buildEventSchemaItem } from '@/utils/eventSchema';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Calendar, 
  MapPin, 
  Copy, 
  Check, 
  ArrowRight, 
  Loader2,
  Building2,
  User,
  Tag
} from 'lucide-react';
import { trackCalendarSubscribe, trackEvent as trackAnalyticsEvent } from '@/utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GA Event Tracking helper (legacy - kept for compatibility)
 */
const trackEvent = (eventName, params) => {
  trackAnalyticsEvent(eventName, params);
};

/**
 * Tag slug mapping for SEO-friendly URLs
 */
const TAG_SLUG_MAP = {
  'healthcare': 'Healthcare',
  'technology': 'Technology',
  'ai': 'AI',
  'events-industry': 'Events Industry',
  'meetings': 'Meetings',
  'mice': 'MICE',
  'corporate-travel': 'Corporate Travel',
  'foodservice-hospitality': 'Foodservice & Hospitality',
  'retail': 'Retail',
  'manufacturing': 'Manufacturing',
  'hr': 'HR',
  'learning-development': 'Learning & Development'
};

/**
 * Get display name from slug
 */
const getTagDisplayName = (slug) => {
  return TAG_SLUG_MAP[slug] || slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

/**
 * SubscribePage - SEO-friendly landing page for filtered calendar subscriptions
 * 
 * Supports filter types: city, venue, organiser, tag
 */
export default function SubscribePage() {
  const { filterType, filterValue } = useParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [filterDisplayName, setFilterDisplayName] = useState('');

  // Build API and ICS URLs based on filter type
  const buildUrls = () => {
    let apiParam = '';
    let icsParam = '';
    let displayName = '';
    
    switch (filterType) {
      case 'city':
        apiParam = `city=${filterValue}`;
        icsParam = `city=${filterValue}`;
        displayName = filterValue.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        break;
      case 'venue':
        apiParam = `venue=${filterValue}`;
        icsParam = `venue=${filterValue}`;
        displayName = filterValue.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        break;
      case 'organiser':
        apiParam = `organizerId=${encodeURIComponent(filterValue)}`;
        icsParam = `organizerId=${encodeURIComponent(filterValue)}`;
        displayName = filterValue;
        break;
      case 'tag':
        const tagName = getTagDisplayName(filterValue);
        apiParam = `tags=${encodeURIComponent(tagName)}`;
        icsParam = `tags=${encodeURIComponent(tagName)}`;
        displayName = tagName;
        break;
      default:
        displayName = 'All';
    }
    
    return {
      apiUrl: `${BACKEND_URL}/api/public/embed/events?${apiParam}&limit=20`,
      icsUrl: `${BACKEND_URL}/api/public/calendar.ics?${icsParam}`,
      displayName
    };
  };

  const { apiUrl, icsUrl, displayName } = buildUrls();

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await axios.get(apiUrl);
        setEvents(response.data.events || []);
        setFilterDisplayName(displayName);
        
        // Track page view
        trackEvent('calendar_subscribe_page_viewed', {
          type: filterType,
          filter: filterValue
        });
      } catch (err) {
        console.error('Error fetching events:', err);
        setError('Unable to load events for this filter.');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [filterType, filterValue, apiUrl, displayName]);

  const handleCopyIcsLink = async () => {
    try {
      await navigator.clipboard.writeText(icsUrl);
      setCopied(true);
      toast.success('Feed link copied! Paste this into your calendar app.');
      
      // Track click
      trackEvent('calendar_ics_subscribe_clicked', {
        type: filterType,
        filter: filterValue,
        eventCount: events.length
      });
      
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy link. Please try again.');
    }
  };

  // Get icon based on filter type
  const getFilterIcon = () => {
    switch (filterType) {
      case 'city':
        return <MapPin className="w-8 h-8 text-orange-500" />;
      case 'venue':
        return <Building2 className="w-8 h-8 text-orange-500" />;
      case 'organiser':
        return <User className="w-8 h-8 text-orange-500" />;
      case 'tag':
        return <Tag className="w-8 h-8 text-orange-500" />;
      default:
        return <Calendar className="w-8 h-8 text-orange-500" />;
    }
  };

  // Generate page title and description
  const getPageMeta = () => {
    const base = filterDisplayName || displayName;
    
    switch (filterType) {
      case 'city':
        return {
          title: `Subscribe to ${base} Events – Jigsaw Conferences`,
          description: `Get live updates and subscribe to upcoming business events in ${base}. Add to Google Calendar, Outlook or Apple Calendar.`,
          h1: `📅 Subscribe to ${base}&apos;s Upcoming Business Events`
        };
      case 'venue':
        return {
          title: `Subscribe to ${base} Events – Jigsaw Conferences`,
          description: `Get live updates and subscribe to upcoming events at ${base}. Add to Google Calendar, Outlook or Apple Calendar.`,
          h1: `📅 Subscribe to Events at ${base}`
        };
      case 'organiser':
        return {
          title: `Subscribe to ${base} Events – Jigsaw Conferences`,
          description: `Get live updates and subscribe to upcoming events organised by ${base}. Add to Google Calendar, Outlook or Apple Calendar.`,
          h1: `📅 Subscribe to Events by ${base}`
        };
      case 'tag':
        return {
          title: `Subscribe to ${base} Events – Jigsaw Conferences`,
          description: `Get live updates and subscribe to upcoming ${base} industry events. Add to Google Calendar, Outlook or Apple Calendar.`,
          h1: `📅 Subscribe to ${base} Events`
        };
      default:
        return {
          title: 'Subscribe to Events – Jigsaw Conferences',
          description: 'Subscribe to upcoming business events and get live updates in your calendar.',
          h1: '📅 Subscribe to Upcoming Events'
        };
    }
  };

  const pageMeta = getPageMeta();

  // JSON-LD Schema
  const generateSchema = () => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": pageMeta.title,
      "description": pageMeta.description,
      "url": `https://events.jigsawconferences.co.uk/subscribe/${filterType}/${filterValue}`
    };
    
    if (events.length > 0) {
      schema.mainEntity = buildEventSchemaItem(events[0]);
    }
    
    return schema;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading events...</span>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEOHead
        title={pageMeta.title}
        description={pageMeta.description}
        url={`https://events.jigsawconferences.co.uk/subscribe/${filterType}/${filterValue}`}
        schema={generateSchema()}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-orange-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white/20 p-4 rounded-full">
              {getFilterIcon()}
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            {pageMeta.h1.replace('&apos;', "'")}
          </h1>
          
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Get these events added to your calendar. This feed updates automatically 
            when we add new events that match this filter.
          </p>
          
          {/* Subscribe Button */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={handleCopyIcsLink}
              size="lg"
              className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-lg font-medium"
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5 mr-2 text-green-600" />
                  Link Copied!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5 mr-2" />
                  Copy Calendar Feed Link
                </>
              )}
            </Button>
            
            <a href={icsUrl} className="text-white/80 hover:text-white text-sm underline">
              Or download .ics file directly
            </a>
          </div>
          
          <p className="text-sm text-blue-200 mt-6">
            {events.length} upcoming event{events.length !== 1 ? 's' : ''} in this feed
          </p>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            How to Subscribe
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="text-center p-6">
              <div className="text-4xl mb-3">📋</div>
              <h3 className="font-semibold mb-2">1. Copy the Link</h3>
              <p className="text-sm text-gray-600">
                Click the button above to copy the calendar feed URL
              </p>
            </Card>
            
            <Card className="text-center p-6">
              <div className="text-4xl mb-3">📅</div>
              <h3 className="font-semibold mb-2">2. Open Your Calendar</h3>
              <p className="text-sm text-gray-600">
                Go to Google Calendar, Outlook, or Apple Calendar settings
              </p>
            </Card>
            
            <Card className="text-center p-6">
              <div className="text-4xl mb-3">✅</div>
              <h3 className="font-semibold mb-2">3. Add Subscription</h3>
              <p className="text-sm text-gray-600">
                Paste the URL to subscribe. Events update automatically!
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Events Preview Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            Events in This Feed
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Preview of upcoming events you&apos;ll receive in your calendar
          </p>
          
          {error ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{error}</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No upcoming events found for this filter.</p>
              <p className="text-sm text-gray-400 mt-2">
                Subscribe anyway – we&apos;ll add events as they&apos;re announced!
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.slice(0, 9).map((event) => (
                <Link
                  key={event.slug || event.name}
                  to={`/events/${event.citySlug}/${event.venueSlug}/${event.slug}`}
                  className="block group"
                >
                  <Card className="h-full hover:shadow-lg transition-shadow border-gray-200 hover:border-orange-500">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-gray-900 mb-3 group-hover:text-orange-600 line-clamp-2">
                        {event.name}
                      </h3>
                      
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <Calendar className="w-4 h-4 mr-2 text-orange-500 flex-shrink-0" />
                          <span>{event.dates?.display || event.dates?.start}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPin className="w-4 h-4 mr-2 text-orange-500 flex-shrink-0" />
                          <span className="truncate">{event.venueName}, {event.city}</span>
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                        {event.shortDescription}
                      </p>
                      
                      <div className="flex items-center text-sm font-medium text-orange-600 group-hover:text-orange-700">
                        View details
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
          
          {events.length > 9 && (
            <p className="text-center text-gray-500 mt-6">
              +{events.length - 9} more event{events.length - 9 !== 1 ? 's' : ''} in this feed
            </p>
          )}
        </div>
      </section>

      {/* Related Subscriptions */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Other Calendar Feeds
          </h2>
          
          <div className="flex flex-wrap justify-center gap-3">
            {filterType !== 'city' && (
              <>
                <Link to="/subscribe/city/london">
                  <Badge variant="outline" className="px-4 py-2 text-sm hover:bg-orange-50 hover:border-orange-500 cursor-pointer">
                    London Events
                  </Badge>
                </Link>
                <Link to="/subscribe/city/birmingham">
                  <Badge variant="outline" className="px-4 py-2 text-sm hover:bg-orange-50 hover:border-orange-500 cursor-pointer">
                    Birmingham Events
                  </Badge>
                </Link>
                <Link to="/subscribe/city/manchester">
                  <Badge variant="outline" className="px-4 py-2 text-sm hover:bg-orange-50 hover:border-orange-500 cursor-pointer">
                    Manchester Events
                  </Badge>
                </Link>
              </>
            )}
            {filterType !== 'venue' && (
              <>
                <Link to="/subscribe/venue/excel-london">
                  <Badge variant="outline" className="px-4 py-2 text-sm hover:bg-orange-50 hover:border-orange-500 cursor-pointer">
                    ExCeL London
                  </Badge>
                </Link>
                <Link to="/subscribe/venue/nec-birmingham">
                  <Badge variant="outline" className="px-4 py-2 text-sm hover:bg-orange-50 hover:border-orange-500 cursor-pointer">
                    NEC Birmingham
                  </Badge>
                </Link>
              </>
            )}
            {filterType !== 'tag' && (
              <>
                <Link to="/subscribe/tag/technology">
                  <Badge variant="outline" className="px-4 py-2 text-sm hover:bg-orange-50 hover:border-orange-500 cursor-pointer">
                    Technology Events
                  </Badge>
                </Link>
                <Link to="/subscribe/tag/healthcare">
                  <Badge variant="outline" className="px-4 py-2 text-sm hover:bg-orange-50 hover:border-orange-500 cursor-pointer">
                    Healthcare Events
                  </Badge>
                </Link>
              </>
            )}
          </div>
          
          <div className="mt-8">
            <Link
              to="/events"
              className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium"
            >
              Browse all events in the Events Hub
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Need Group Accommodation?
          </h2>
          <p className="text-orange-100 mb-6 max-w-2xl mx-auto">
            Jigsaw Conferences arranges hotels, serviced apartments and meeting rooms 
            for teams attending business events – free of charge.
          </p>
          <Link to="/events">
            <Button 
              size="lg"
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-orange-600"
            >
              Find Your Event
            </Button>
          </Link>
        </div>
      </section>
    </MainLayout>
  );
}
